import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { getBrand } from '../../config/brand.config';
import { getOgLocale, getOgLocaleAlternates } from '../../config/seo-i18n';
import {
  buildTitle,
  getCanonical,
  getSeoForPath,
  getSiteName,
  getSiteUrl,
  getDefaultDesc,
  DEFAULT_KEYWORDS,
  type SeoMeta,
} from '../../config/seo.config';
import { SEO_CURRENCIES, currencyGatewayPath } from '../../config/seo-currencies';

export interface PageMetaProps extends Partial<SeoMeta> {
  title?: string;
  description?: string;
  keywords?: string;
  canonical?: string;
  ogImage?: string;
  ogType?: 'website' | 'article';
  noIndex?: boolean;
  override?: boolean;
}

function defaultOgImage(): string {
  return getBrand().iconPng512;
}

/** Trim keywords to SEO-friendly length (~10–15 terms). */
function normalizeKeywords(raw?: string): string | undefined {
  if (!raw) return undefined;
  const parts = raw.split(/[,;|]/).map((s) => s.trim()).filter(Boolean);
  const unique = [...new Set(parts)];
  return unique.slice(0, 15).join(', ');
}

/** Page-specific JSON-LD only — global Organization/WebSite loaded once via GlobalSiteSchema. */
function injectPageJsonLd(pathname: string, fullTitle: string, description: string, canonical: string) {
  const existing = document.querySelector('script[data-seo-page-jsonld]');
  if (existing) existing.remove();

  const siteUrl = getSiteUrl();
  const pathOnly = (pathname.split('?')[0] || '/').replace(/\/$/, '') || '/';

  const breadcrumbs = (crumbs: { name: string; path: string }[]) => ({
    '@type': 'BreadcrumbList',
    '@id': `${canonical}#breadcrumb`,
    itemListElement: crumbs.map((c, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: c.name,
      item: c.path.startsWith('http') ? c.path : `${siteUrl}${c.path === '/' ? '' : c.path}`,
    })),
  });

  const webPageType =
    pathOnly === '/contact'
      ? 'ContactPage'
      : pathOnly === '/about'
        ? 'AboutPage'
        : pathOnly === '/blog' || pathOnly.startsWith('/blog/category/') || pathOnly.startsWith('/blog/tag/') || pathOnly === '/apps'
          ? 'CollectionPage'
          : 'WebPage';

  const graph: Record<string, unknown>[] = [
    {
      '@type': webPageType,
      '@id': `${canonical}#webpage`,
      url: canonical,
      name: fullTitle,
      description,
      isPartOf: { '@id': `${siteUrl}/#website` },
      about: { '@id': `${siteUrl}/#payment-service` },
      publisher: { '@id': `${siteUrl}/#organization` },
    },
  ];

  if (pathOnly === '/') {
    graph.push({
      '@type': 'FAQPage',
      '@id': `${canonical}#faq`,
      mainEntity: [
        {
          '@type': 'Question',
          name: 'What is tropayment?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'tropayment is a cryptocurrency payment gateway for merchants to accept USDT, USDC, and native coins on TRON, Ethereum, BSC, Polygon, and Solana.',
          },
        },
        {
          '@type': 'Question',
          name: 'Which networks does tropayment support?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Supported networks include TRON, Ethereum, BSC, Polygon, and Solana with stablecoins and native assets.',
          },
        },
        {
          '@type': 'Question',
          name: 'Does tropayment provide a merchant API?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Yes. tropayment offers a REST merchant API with HMAC authentication, webhooks, and hosted checkout.',
          },
        },
      ],
    });
    graph.push(breadcrumbs([{ name: 'Home', path: '/' }]));
  } else if (pathOnly === '/contact') {
    graph.push({
      '@type': 'ContactPage',
      '@id': `${canonical}#contactpage`,
      name: fullTitle,
      description,
      url: canonical,
      isPartOf: { '@id': `${siteUrl}/#website` },
    });
    graph.push(breadcrumbs([
      { name: 'Home', path: '/' },
      { name: 'Contact', path: '/contact' },
    ]));
  } else if (pathOnly === '/features') {
    graph.push({
      '@type': 'ItemList',
      '@id': `${canonical}#features`,
      name: 'Crypto payment gateway features',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Multi-chain USDT checkout' },
        { '@type': 'ListItem', position: 2, name: 'Merchant REST API & webhooks' },
        { '@type': 'ListItem', position: 3, name: 'HD wallets & QR payments' },
      ],
    });
    graph.push(breadcrumbs([
      { name: 'Home', path: '/' },
      { name: 'Features', path: '/features' },
    ]));
  } else if (pathOnly === '/pricing') {
    graph.push({
      '@type': 'WebPage',
      '@id': `${canonical}#pricing`,
      name: fullTitle,
      description,
      url: canonical,
      about: { '@id': `${siteUrl}/#payment-service` },
    });
    graph.push(breadcrumbs([
      { name: 'Home', path: '/' },
      { name: 'Pricing', path: '/pricing' },
    ]));
  } else if (pathOnly === '/documentation') {
    graph.push({
      '@type': 'TechArticle',
      '@id': `${canonical}#docs`,
      headline: fullTitle,
      description,
      url: canonical,
      author: { '@id': `${siteUrl}/#organization` },
      publisher: { '@id': `${siteUrl}/#organization` },
    });
    graph.push(breadcrumbs([
      { name: 'Home', path: '/' },
      { name: 'Documentation', path: '/documentation' },
    ]));
  } else if (pathOnly === '/guide') {
    graph.push({
      '@type': 'TechArticle',
      '@id': `${canonical}#guide`,
      headline: fullTitle,
      description,
      url: canonical,
      author: { '@id': `${siteUrl}/#organization` },
      publisher: { '@id': `${siteUrl}/#organization` },
    });
    graph.push(breadcrumbs([
      { name: 'Home', path: '/' },
      { name: 'User Guide', path: '/guide' },
    ]));
  } else if (pathOnly === '/blog') {
    graph.push({
      '@type': 'Blog',
      '@id': `${canonical}#blog`,
      name: fullTitle,
      description,
      url: canonical,
      publisher: { '@id': `${siteUrl}/#organization` },
    });
    graph.push(breadcrumbs([
      { name: 'Home', path: '/' },
      { name: 'Blog', path: '/blog' },
    ]));
  } else if (pathOnly === '/apps') {
    graph.push({
      '@type': 'CollectionPage',
      '@id': `${canonical}#apps`,
      name: fullTitle,
      description,
      url: canonical,
      isPartOf: { '@id': `${siteUrl}/#website` },
    });
    graph.push(breadcrumbs([
      { name: 'Home', path: '/' },
      { name: 'Apps & Plugins', path: '/apps' },
    ]));
  } else if (pathOnly.startsWith('/apps/') && pathOnly !== '/apps') {
    graph.push(breadcrumbs([
      { name: 'Home', path: '/' },
      { name: 'Apps', path: '/apps' },
      { name: fullTitle, path: pathOnly },
    ]));
  } else if (pathOnly.startsWith('/blog/category/')) {
    graph.push({
      '@type': 'CollectionPage',
      '@id': `${canonical}#category`,
      name: fullTitle,
      description,
      url: canonical,
      isPartOf: { '@id': `${siteUrl}/blog#blog` },
    });
    graph.push(breadcrumbs([
      { name: 'Home', path: '/' },
      { name: 'Blog', path: '/blog' },
      { name: fullTitle, path: pathOnly },
    ]));
  } else if (pathOnly.startsWith('/blog/tag/')) {
    graph.push({
      '@type': 'CollectionPage',
      '@id': `${canonical}#tag`,
      name: fullTitle,
      description,
      url: canonical,
      isPartOf: { '@id': `${siteUrl}/blog#blog` },
    });
    graph.push(breadcrumbs([
      { name: 'Home', path: '/' },
      { name: 'Blog', path: '/blog' },
      { name: fullTitle, path: pathOnly },
    ]));
  } else if (pathOnly.startsWith('/blog/') && pathOnly !== '/blog') {
    // BlogPosting is injected by BlogArticlePage; keep breadcrumbs here.
    graph.push(breadcrumbs([
      { name: 'Home', path: '/' },
      { name: 'Blog', path: '/blog' },
      { name: fullTitle, path: pathOnly },
    ]));
  } else if (pathOnly === '/about') {
    graph.push({
      '@type': 'ItemList',
      '@id': `${canonical}#currencies`,
      name: 'Supported cryptocurrency payment gateways',
      itemListElement: SEO_CURRENCIES.map((currency, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: `${currency.name} (${currency.symbol}) Payment Gateway`,
        url: `${siteUrl}${currencyGatewayPath(currency.slug)}`,
      })),
    });
    graph.push(breadcrumbs([
      { name: 'Home', path: '/' },
      { name: 'About', path: '/about' },
    ]));
  } else if (pathOnly.startsWith('/page/')) {
    graph.push(breadcrumbs([
      { name: 'Home', path: '/' },
      { name: fullTitle, path: pathOnly },
    ]));
  } else {
    // Programmatic SEO and other public paths — always emit breadcrumbs.
    const segments = pathOnly.split('/').filter(Boolean);
    const crumbs = [{ name: 'Home', path: '/' }];
    let acc = '';
    for (const seg of segments) {
      acc += `/${seg}`;
      crumbs.push({ name: seg.replace(/-/g, ' '), path: acc });
    }
    graph.push(breadcrumbs(crumbs));
  }

  const script = document.createElement('script');
  script.setAttribute('type', 'application/ld+json');
  script.setAttribute('data-seo-page-jsonld', 'true');
  script.textContent = JSON.stringify({
    '@context': 'https://schema.org',
    '@graph': graph,
  });
  document.head.appendChild(script);
}

/** Resolve canonical: always self-URL for the current path (never homepage bleed). */
function resolvePageCanonical(pathname: string, search: string, propCanonical?: string): string {
  const fromPath = getCanonical(pathname, search);
  if (!propCanonical) return fromPath;

  try {
    const propUrl = new URL(propCanonical, getSiteUrl());
    const propPath = propUrl.pathname.replace(/\/+$/, '') || '/';
    const curPath = (pathname.split('?')[0] || '/').replace(/\/+$/, '') || '/';
    // Allow prop only when it targets the same path (e.g. blog pagination / cleaned slug).
    if (propPath === curPath) {
      return getCanonical(propPath, propUrl.search || search);
    }
  } catch {
    // ignore invalid prop
  }

  return fromPath;
}

/**
 * Unified SEO for all pages. Meta tags for Google and social networks.
 */
export default function PageMeta({
  title,
  description,
  keywords,
  canonical,
  ogImage,
  ogType = 'website',
  noIndex,
  override = false,
}: PageMetaProps) {
  const { pathname, search } = useLocation();
  const { i18n } = useTranslation();
  const routeSeo = override ? null : getSeoForPath(pathname, i18n.language);

  const meta: SeoMeta = {
    title: title ?? routeSeo?.title ?? '',
    description: description ?? routeSeo?.description ?? getDefaultDesc(i18n.language),
    keywords: normalizeKeywords(keywords ?? routeSeo?.keywords ?? DEFAULT_KEYWORDS),
    canonical: resolvePageCanonical(pathname, search, canonical ?? routeSeo?.canonical),
    ogImage: ogImage ?? routeSeo?.ogImage ?? defaultOgImage(),
    noIndex: noIndex ?? routeSeo?.noIndex ?? false,
  };

  useEffect(() => {
    const fullTitle = buildTitle(meta.title);
    const desc = (meta.description || getDefaultDesc(i18n.language)).slice(0, 160);
    const siteUrl = getSiteUrl();
    const siteName = getSiteName();
    const canon = resolvePageCanonical(pathname, search, meta.canonical);

    document.title = fullTitle;
    document.documentElement.lang = i18n.language || 'en';
    document.documentElement.dir = i18n.language === 'ar' ? 'rtl' : 'ltr';

    const setMeta = (attr: 'name' | 'property', key: string, content: string) => {
      let el = document.querySelector(attr === 'name' ? `meta[name="${key}"]` : `meta[property="${key}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    const removeMeta = (attr: 'name' | 'property', key: string) => {
      const el = document.querySelector(attr === 'name' ? `meta[name="${key}"]` : `meta[property="${key}"]`);
      if (el) el.remove();
    };

    setMeta('name', 'description', desc);
    if (meta.keywords) setMeta('name', 'keywords', meta.keywords);
    else removeMeta('name', 'keywords');

    let canonicalEl = document.querySelector('link[rel="canonical"]');
    if (!canonicalEl) {
      canonicalEl = document.createElement('link');
      canonicalEl.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalEl);
    }
    canonicalEl.setAttribute('href', canon);

    // Language is client-side only (no localized URL paths) — do not emit
    // multiple hreflang pointing at the same URL (misleading for Google).
    document.querySelectorAll('link[rel="alternate"][hreflang]').forEach((el) => el.remove());
    const xDefault = document.createElement('link');
    xDefault.setAttribute('rel', 'alternate');
    xDefault.setAttribute('hreflang', 'x-default');
    xDefault.setAttribute('href', canon);
    document.head.appendChild(xDefault);

    const imgUrl = meta.ogImage?.startsWith('http') ? meta.ogImage : `${siteUrl}${meta.ogImage}`;

    setMeta('property', 'og:title', fullTitle);
    setMeta('property', 'og:description', desc);
    setMeta('property', 'og:type', ogType);
    setMeta('property', 'og:url', canon);
    setMeta('property', 'og:site_name', siteName);
    setMeta('property', 'og:image', imgUrl);
    setMeta('property', 'og:locale', getOgLocale(i18n.language));
    document.querySelectorAll('meta[property="og:locale:alternate"]').forEach((el) => el.remove());
    for (const alt of getOgLocaleAlternates(i18n.language)) {
      const el = document.createElement('meta');
      el.setAttribute('property', 'og:locale:alternate');
      el.setAttribute('content', alt);
      document.head.appendChild(el);
    }

    setMeta('name', 'twitter:card', 'summary_large_image');
    setMeta('name', 'twitter:title', fullTitle);
    setMeta('name', 'twitter:description', desc);
    setMeta('name', 'twitter:image', imgUrl);

    if (meta.noIndex) {
      let robots = document.querySelector('meta[name="robots"]');
      if (!robots) {
        robots = document.createElement('meta');
        robots.setAttribute('name', 'robots');
        document.head.appendChild(robots);
      }
      robots.setAttribute('content', 'noindex, nofollow');
    } else {
      const robots = document.querySelector('meta[name="robots"]');
      if (robots) robots.remove();
      setMeta('name', 'robots', 'index, follow, max-image-preview:large');
      injectPageJsonLd(pathname, fullTitle, desc, canon);
    }
  }, [pathname, search, i18n.language, meta.title, meta.description ?? '', meta.keywords ?? '', meta.canonical ?? '', meta.ogImage ?? '', meta.noIndex ?? false, ogType]);

  return null;
}
